// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// GOODS RECEIPT RECORD TAB
// *******************************************************************************
// Main tab containing the collapse cards for goods receipt:
// 1. NfeIdentificationCard - Document number, serie, dates, access key
// 2. IssuerCard - Supplier (issuer) selection
// 3. ItemsCard - Line items table with add/edit/remove via ItemModal
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { Collapse, Form, Typography, Space, Tooltip, Button, Tag } from 'antd';
import {
  SettingFilled,
  UserOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  EditOutlined,
  FileTextOutlined,
  CarOutlined,
  CalculatorOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import NfeIdentificationCard from './NfeIdentificationCard';
import IssuerCard from './IssuerCard';
import ItemsCard from './ItemsCard';
import CarrierCard from './CarrierCard';
import TotalsCard from './TotalsCard';
import PaymentBillingCard from './PaymentBillingCard';

const { Text } = Typography;

// Collapse shared styles
const COLLAPSE_STYLE = { marginTop: 8, marginBottom: 12 };
const COLLAPSE_STYLE_NO_TOP = { marginBottom: 12 };
const COLLAPSE_STYLES = {
  header: { alignItems: 'center' },
  body: { padding: 20 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const GoodsReceiptRecordTab = ({
  items,
  setItems,
  installments,
  setInstallments,
  defaultOpenNfe = true,
  defaultOpenIssuer,
  defaultOpenItems,
  defaultOpenCarrier,
  defaultOpenTotals,
  defaultOpenPayment,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading, viewMode } = useDocumentContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};

  // Watch documentType to update Sefaz model label reactively
  const formDocumentType = Form.useWatch('documentType', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !data?._id || viewMode === 'add';
  const effectiveDocumentType = formDocumentType || data?.documentType || 'nfe';
  const isNfce = effectiveDocumentType === 'nfce';
  const sefazModelLabel = isNfce ? 'Sefaz Modelo 65' : 'Sefaz Modelo 55';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [nfeCollapseKeys, setNfeCollapseKeys] = useState([]);
  const [issuerCollapseKeys, setIssuerCollapseKeys] = useState([]);
  const [itemsCollapseKeys, setItemsCollapseKeys] = useState([]);
  const [carrierCollapseKeys, setCarrierCollapseKeys] = useState([]);
  const [totalsCollapseKeys, setTotalsCollapseKeys] = useState([]);
  const [paymentCollapseKeys, setPaymentCollapseKeys] = useState([]);
  const [collapseInitialized, setCollapseInitialized] = useState(false);
  // Actions from IssuerCard (for rendering buttons in Collapse header)
  const [issuerActions, setIssuerActions] = useState(null);
  // Actions from ItemsCard (for rendering add button in Collapse header)
  const [itemsActions, setItemsActions] = useState(null);
  // Actions from CarrierCard
  const [carrierActions, setCarrierActions] = useState(null);
  // Actions from PaymentBillingCard
  const [paymentActions, setPaymentActions] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Open collapse panels based on props and document status
  // Props (defaultOpenNfe, defaultOpenIssuer, defaultOpenItems) override defaults.
  // When undefined, defaults to: all open for new/draft, only nfe for confirmed/cancelled.
  useEffect(() => {
    if (collapseInitialized) return;
    if (!data?._id && !isNew) return;

    setCollapseInitialized(true);

    const isDraft = isNew || data?.status === 'draft';

    // Resolve each panel: prop > default closed
    const openNfe = defaultOpenNfe ?? false;
    const openIssuer = defaultOpenIssuer ?? false;
    const openItems = defaultOpenItems ?? false;
    const openCarrier = defaultOpenCarrier ?? false;
    const openTotals = defaultOpenTotals ?? false;
    const openPayment = defaultOpenPayment ?? false;

    if (openNfe) setNfeCollapseKeys(['nfeIdentification']);
    if (openIssuer) setIssuerCollapseKeys(['issuerInfo']);
    if (openItems) setItemsCollapseKeys(['itemsList']);
    if (openCarrier) setCarrierCollapseKeys(['carrierInfo']);
    if (openTotals) setTotalsCollapseKeys(['totalsInfo']);
    if (openPayment) setPaymentCollapseKeys(['paymentInfo']);
  }, [data?._id, data?.status, collapseInitialized, isNew, defaultOpenNfe, defaultOpenIssuer, defaultOpenItems, defaultOpenCarrier, defaultOpenTotals, defaultOpenPayment]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // Handle actions from IssuerCard
  const handleIssuerActionsChange = useCallback(actions => {
    setIssuerActions(actions);
  }, []);

  // Handle actions from ItemsCard
  const handleItemsActionsChange = useCallback(actions => {
    setItemsActions(actions);
  }, []);

  // Handle actions from CarrierCard
  const handleCarrierActionsChange = useCallback(actions => {
    setCarrierActions(actions);
  }, []);

  // Handle actions from PaymentBillingCard
  const handlePaymentActionsChange = useCallback(actions => {
    setPaymentActions(actions);
  }, []);

  // Items management handlers
  const handleAddItem = useCallback(
    newItem => {
      setItems(prev => [...prev, { ...newItem, itemNumber: prev.length + 1 }]);
    },
    [setItems],
  );

  const handleUpdateItem = useCallback(
    (index, updatedItem) => {
      setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...updatedItem } : item)));
    },
    [setItems],
  );

  const handleRemoveItem = useCallback(
    index => {
      setItems(prev =>
        prev
          .filter((_, i) => i !== index)
          .map((item, i) => ({ ...item, itemNumber: i + 1 })),
      );
    },
    [setItems],
  );

  // Installments management handlers
  const handleAddInstallment = useCallback(
    newInst => {
      setInstallments(prev => [...prev, newInst]);
    },
    [setInstallments],
  );

  const handleUpdateInstallment = useCallback(
    (index, updatedInst) => {
      setInstallments(prev =>
        prev.map((inst, i) => (i === index ? { ...inst, ...updatedInst } : inst)),
      );
    },
    [setInstallments],
  );

  const handleRemoveInstallment = useCallback(
    index => {
      setInstallments(prev => prev.filter((_, i) => i !== index));
    },
    [setInstallments],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isConfirmed = data?.status === 'confirmed';

  // Issuer action buttons for Collapse header (new + edit entity)
  const issuerHeaderExtra =
    !isLocked && !isConfirmed && issuerActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new_issuer')} placement="top">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={issuerActions.handleNewRecipient}
          />
        </Tooltip>
        <Tooltip title={translateX('edit_issuer')} placement="top">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={issuerActions.handleEditRecipient}
            disabled={!issuerActions.selectedRecipient || issuerActions.isSystemGenericEntity}
          />
        </Tooltip>
      </Space>
    ) : null;

  // Items action buttons for Collapse header (add item)
  const itemsHeaderExtra =
    !isLocked && !isConfirmed ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new_item')} placement="top">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => itemsActions?.handleAddClick?.()}
          />
        </Tooltip>
      </Space>
    ) : null;

  // Carrier action buttons for Collapse header (new + edit entity)
  const carrierHeaderExtra =
    !isLocked && !isConfirmed && carrierActions ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('new')} placement="top">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={carrierActions.handleNewCarrier}
          />
        </Tooltip>
        <Tooltip title={translateX('edit')} placement="top">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={carrierActions.handleEditCarrier}
            disabled={!carrierActions.selectedCarrier}
          />
        </Tooltip>
      </Space>
    ) : null;

  // Payment action buttons for Collapse header (add installment)
  const paymentHeaderExtra =
    !isLocked && !isConfirmed ? (
      <Space size={4} onClick={e => e.stopPropagation()}>
        <Tooltip title={translateX('add_installment')} placement="top">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => paymentActions?.handleAddClick?.()}
          />
        </Tooltip>
      </Space>
    ) : null;

  // Items count tag for Collapse label
  const itemsCount = items?.length || 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="goodsReceiptRecord" isFetching={loading} data={data} />

      {/* Card 1: NFe Identification */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE}
        styles={COLLAPSE_STYLES}
        activeKey={nfeCollapseKeys}
        onChange={keys => setNfeCollapseKeys(keys)}
        items={[
          {
            key: 'nfeIdentification',
            forceRender: true,
            label: buildCollapseLabel(
              <FileTextOutlined  style={{ marginRight: 6 }} />,
              translateX('header'),
            ),
            extra: (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {sefazModelLabel}
              </Text>
            ),
            children: <NfeIdentificationCard data={data} viewMode={viewMode} />,
          },
        ]}
      />

      {/* Card 2: Issuer (Emissor) */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE_NO_TOP}
        styles={COLLAPSE_STYLES}
        activeKey={issuerCollapseKeys}
        onChange={keys => setIssuerCollapseKeys(keys)}
        items={[
          {
            key: 'issuerInfo',
            forceRender: true,
            label: buildCollapseLabel(
              <UserOutlined style={{ marginRight: 6 }} />,
              translateX('issuer'),
            ),
            extra: issuerHeaderExtra,
            children: (
              <IssuerCard
                data={data}
                viewMode={viewMode}
                onActionsChange={handleIssuerActionsChange}
              />
            ),
          },
        ]}
      />

      {/* Card 3: Items */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE_NO_TOP}
        styles={COLLAPSE_STYLES}
        activeKey={itemsCollapseKeys}
        onChange={keys => setItemsCollapseKeys(keys)}
        items={[
          {
            key: 'itemsList',
            forceRender: true,
            label: buildCollapseLabel(
              <ShoppingCartOutlined style={{ marginRight: 6 }} />,
              <>
                {translateX('items')}
              </>,
            ),
            extra: itemsHeaderExtra,
            children: (
              <ItemsCard
                items={items}
                onAddItem={handleAddItem}
                onUpdateItem={handleUpdateItem}
                onRemoveItem={handleRemoveItem}
                viewMode={viewMode}
                data={data}
                onActionsChange={handleItemsActionsChange}
              />
            ),
          },
        ]}
      />

      {/* Card 4: Carrier (Transportadora) — NFe only, hidden for NFCe */}
      {!isNfce && (
        <Collapse
          size="small"
          style={COLLAPSE_STYLE_NO_TOP}
          styles={COLLAPSE_STYLES}
          activeKey={carrierCollapseKeys}
          onChange={keys => setCarrierCollapseKeys(keys)}
          items={[
            {
              key: 'carrierInfo',
              forceRender: true,
              label: buildCollapseLabel(
                <CarOutlined style={{ marginRight: 6 }} />,
                translateX('logistics_transport'),
              ),
              extra: carrierHeaderExtra,
              children: (
                <CarrierCard
                  data={data}
                  viewMode={viewMode}
                  onActionsChange={handleCarrierActionsChange}
                />
              ),
            },
          ]}
        />
      )}

      {/* Card 5: Totals (Resumo da Nota) */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE_NO_TOP}
        styles={COLLAPSE_STYLES}
        activeKey={totalsCollapseKeys}
        onChange={keys => setTotalsCollapseKeys(keys)}
        items={[
          {
            key: 'totalsInfo',
            forceRender: true,
            label: buildCollapseLabel(
              <CalculatorOutlined style={{ marginRight: 6 }} />,
              translateX('document_totals'),
            ),
            children: (
              <TotalsCard
                items={items}
                data={data}
                viewMode={viewMode}
              />
            ),
          },
        ]}
      />

      {/* Card 6: Payment & Billing (Fatura e Pagamentos) */}
      <Collapse
        size="small"
        style={COLLAPSE_STYLE_NO_TOP}
        styles={COLLAPSE_STYLES}
        activeKey={paymentCollapseKeys}
        onChange={keys => setPaymentCollapseKeys(keys)}
        items={[
          {
            key: 'paymentInfo',
            forceRender: true,
            label: buildCollapseLabel(
              <DollarOutlined style={{ marginRight: 6 }} />,
              translateX('payment_billing'),
            ),
            extra: paymentHeaderExtra,
            children: (
              <PaymentBillingCard
                installments={installments}
                onAddInstallment={handleAddInstallment}
                onUpdateInstallment={handleUpdateInstallment}
                onRemoveInstallment={handleRemoveInstallment}
                viewMode={viewMode}
                data={data}
                onActionsChange={handlePaymentActionsChange}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default GoodsReceiptRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildCollapseLabel(icon, text) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 40,
      }}
    >
      {icon}
      {text}
    </span>
  );
}
